import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchMediaKeys,
  fetchMediaUrls,
  fetchProctoringDetails,
  checkReportAvailability,
  fetchProgressionTimeline,
  // Import the action type constant directly
  RESET_MEDIA_STATE
} from '../../actions'
import {
  Drawer,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Badge,
  Divider,
  Image,
  Empty,
  Spin,
  Statistic,
  Select,
  Table,
  Tag,
  List,
  message,
  Modal,
  Dropdown,
  Menu,
  Tooltip,
} from 'antd'
import {
  EyeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  GlobalOutlined,
  VideoCameraTwoTone,
  AudioTwoTone,
  AudioFilled,
  VideoCameraFilled,
  DownOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import Avatar from 'components/Avatar'
import AudioVideoModal from '../../../../components/AudioVideoModal'
import ProgressionGraph from '../../components/ProgressionGraph'
import axios from 'axios'
import {
  collectSnapshots,
  primarySession,
  collectIpAddresses,
} from './proctoringView'
import { SINGLE_DIAGNOSIS_ATTEMPT_MESSAGE, isSingleDiagnosisAttempt } from './reportDownloadGate'

const { Title, Text } = Typography
const { Option } = Select

const ScoreCard = styled(Card)`
  text-align: center;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background-color: ${props => {
    // If it's a proficiency level
    if (typeof props.score === 'string') {
      // Use red background for Beginner, green for all others
      return props.score.toLowerCase() === 'beginner' ? '#FBDFE1' : '#BFF4E0'
    } else {
      // Original score-based coloring
      return parseFloat(props.score) <= 25 ? '#FBDFE1' : '#BFF4E0'
    }
  }};
  border: 2px solid
    ${props => {
    // If it's a proficiency level
    if (typeof props.score === 'string') {
      // Use red border for Beginner, green for all others
      return props.score.toLowerCase() === 'beginner' ? '#F54B56' : '#5DB594'
    } else {
      // Original score-based coloring
      return parseFloat(props.score) <= 25 ? '#F54B56' : '#5DB594'
    }
  }};
  height: 150px;
  display: flex;
  flex-direction: column;

  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
  }
`

const ScoreValue = styled.div`
  font-family: 'Plus Jakarta Sans';
  font-weight: 700;
  font-size: 22px;
  line-height: 25px;
  letter-spacing: 0%;
  text-align: center;
  color: '#FFFFFF';
  margin: 12px 0;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ScoreTitle = styled(Title)`
  margin-bottom: 0 !important;
  color: #333333; /* Change title color for better readability */
`

const SubText = styled(Text)`
  color: #666666;
  font-size: 12px;
  white-space: nowrap;
`
const TitleSection = styled.div`
  margin-bottom: 10px;
`

const ValueSection = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SnapshotCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-body {
    padding: 12px;
  }

  .snapshot-image {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 4px;
  }

  .snapshot-info {
    margin-top: 8px;
  }
`

const ProctoringSection = styled.div`
  margin-top: 24px;
`

const StudentReportModal = ({ visible, onClose, student, allowProctoring, isDiagnosis = false, enabledSections: enabledSectionsProp, assessmentType: assessmentTypeProp }) => {
  const dispatch = useDispatch()
  const [proctoringData, setProctoringData] = useState(null)
  const [proctoringLoading, setProctoringLoading] = useState(false)
  const [displayType, setDisplayType] = useState('snapshots') // New state for tracking the selected display type
  const [audioVideoVisible, setAudioVideoVisible] = useState(false)
  const [selectedMediaType, setSelectedMediaType] = useState(null)
  const [downloadingReport, setDownloadingReport] = useState(false)

  // For diagnosis mode: track which assessment is selected (drives proctoring, media, recalculate, etc.)
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null)

  // Progression graph data
  const [commProgression, setCommProgression] = useState([])
  const [aptProgression, setAptProgression] = useState([])
  const [commDelta, setCommDelta] = useState(null)
  const [aptDelta, setAptDelta] = useState(null)
  const [progressionLoading, setProgressionLoading] = useState(false)

  // Determine which progression graphs to show based on assessmentType prop
  const normalizedAssessmentType = (assessmentTypeProp || student?.assessmentType || '').toLowerCase()
  const isCommunicationType = ['communication', 'hinglish'].includes(normalizedAssessmentType)
  const isAptitudeType = normalizedAssessmentType === 'aptitude'

  // Cache fetched data per assessment ID while drawer is open
  const [cache, setCache] = useState({}) // { [assessmentId]: { proctoring, media, reportAvailable } }

  // Initialize selectedAssessmentId when drawer opens in diagnosis mode
  useEffect(() => {
    if (visible && isDiagnosis) {
      const defaultId = student?.assessmentAssignedId1 || student?.assessmentAssignedId2 || null
      setSelectedAssessmentId(defaultId)
    } else if (visible && !isDiagnosis) {
      setSelectedAssessmentId(student?.assessmentAssignedId || null)
    }
    if (!visible) {
      setSelectedAssessmentId(null)
      setCache({}) // Clear cache when drawer closes
    }
  }, [visible, isDiagnosis, student?.assessmentAssignedId, student?.assessmentAssignedId1, student?.assessmentAssignedId2])

  // The effective assessment ID used throughout the drawer
  const effectiveAssessmentId = isDiagnosis ? selectedAssessmentId : student?.assessmentAssignedId

  // Access the media state from Redux
  const mediaState = useSelector(state => state.mediaState)
  const {
    loading: mediaLoading,
    audioAvailable,
    videoAvailable,
    reportAvailable,
    mediaUrls,
    error: mediaError
  } = mediaState


  // A Diagnosis report is the paired result of both papers - block the PDF while
  // only one of the two has been attempted.
  const hasSingleDiagnosisAttempt = isSingleDiagnosisAttempt(student, isDiagnosis)

  // Calculate if report should be downloadable
  const isReportDownloadable = reportAvailable && student?.scoresCalculated !== false && !hasSingleDiagnosisAttempt
  const downloadReportTitle = hasSingleDiagnosisAttempt
    ? SINGLE_DIAGNOSIS_ATTEMPT_MESSAGE
    : student?.scoresCalculated === false
      ? 'Report is not available - scores are being processed'
      : !reportAvailable
        ? 'Report is not available for this assessment'
        : 'Download assessment report'

  // Fetch progression timeline data
  useEffect(() => {
    if (!visible || !student?.email) {
      setCommProgression([])
      setAptProgression([])
      return
    }
    const loadProgression = async () => {
      setProgressionLoading(true)
      try {
        // Only fetch the relevant progression based on assessment type
        const fetchComm = !isAptitudeType
        const fetchApt = !isCommunicationType

        const [commResult, aptResult] = await Promise.all([
          fetchComm ? dispatch(fetchProgressionTimeline(student.email, 'communication', null, effectiveAssessmentId)) : Promise.resolve(null),
          fetchApt ? dispatch(fetchProgressionTimeline(student.email, 'aptitude', null, effectiveAssessmentId)) : Promise.resolve(null),
        ])
        const commTimeline = commResult ? (commResult?.timeline || (Array.isArray(commResult) ? commResult : [])) : []
        const aptTimeline = aptResult ? (aptResult?.timeline || (Array.isArray(aptResult) ? aptResult : [])) : []

        setCommProgression(commTimeline)
        setAptProgression(aptTimeline)
        setCommDelta(commResult?.delta?.assessment || null)
        setAptDelta(aptResult?.delta?.assessment || null)
      } catch (e) {
        console.error('Failed to load progression:', e)
      }
      setProgressionLoading(false)
    }
    loadProgression()
  }, [visible, student?.email, effectiveAssessmentId, dispatch])

  //console.log("student : ", student);

  // Fetch media and report availability when drawer opens or selected assessment changes (with caching)
  useEffect(() => {

    if (visible && effectiveAssessmentId) {
      // Skip fetch if we already have cached data for this assessment
      if (cache[effectiveAssessmentId]?.mediaFetched) {
        // Restore cached media state to Redux
        const cached = cache[effectiveAssessmentId]
        if (cached.proctoringData) {
          setProctoringData(cached.proctoringData)
        }
        return
      }

      // Fetch media keys for the selected assessment
      dispatch(fetchMediaKeys(effectiveAssessmentId))

      const rawStudentNumber = student?.studentNumber ?? student?.studentId ?? student?.id
      const studentNumber = (rawStudentNumber !== null && rawStudentNumber !== undefined && rawStudentNumber !== '-') ? rawStudentNumber : null

      if (studentNumber !== null) {
        dispatch(checkReportAvailability(effectiveAssessmentId, studentNumber))
      } else {
        console.warn('Student number or assessment ID is missing, cannot check report availability');
      }

      // Mark as fetched in cache
      setCache(prev => ({
        ...prev,
        [effectiveAssessmentId]: { ...prev[effectiveAssessmentId], mediaFetched: true }
      }))
    }

    // Reset media state when the component unmounts or drawer closes
    return () => {
      if (!visible) {
        // Dispatch the RESET_MEDIA_STATE action directly instead of using the action creator
        dispatch({ type: RESET_MEDIA_STATE })
      }
    }
  }, [visible, effectiveAssessmentId, student, dispatch])

  // For debugging - log media state changes
  useEffect(() => {
    //console.log('Media state updated:', mediaState)
  }, [mediaState])

  // Restore cached proctoring data when selected assessment changes
  useEffect(() => {
    if (effectiveAssessmentId && cache[effectiveAssessmentId]?.proctoringData) {
      setProctoringData(cache[effectiveAssessmentId].proctoringData)
    } else {
      setProctoringData(null)
    }
  }, [effectiveAssessmentId])

  const handleViewProctoring = async () => {
    try {
      if (!effectiveAssessmentId) {
        console.error('No assessment assigned ID found')
        return
      }

      // Return cached proctoring data if available
      if (cache[effectiveAssessmentId]?.proctoringData) {
        setProctoringData(cache[effectiveAssessmentId].proctoringData)
        return
      }

      setProctoringLoading(true)
      const data = await dispatch(
        fetchProctoringDetails(effectiveAssessmentId)
      )
      setProctoringData(data)
      // Cache the proctoring data
      setCache(prev => ({
        ...prev,
        [effectiveAssessmentId]: { ...prev[effectiveAssessmentId], proctoringData: data }
      }))
    } catch (error) {
      console.error('Failed to fetch proctoring details:', error)
      message.error('Failed to load proctoring data')
    } finally {
      setProctoringLoading(false)
    }
  }


  /**
   * Fetches the media keys (filenames) from the assessment proctor API
   * @returns {Promise<Array>} Array of file objects with filename and type
   */
  const getMediaKeys = async () => {
    try {
      const kvResp = await fetch(
        `${process.env.ADMIN_API_URL}/assessment/getAudioVideoProctoring?assessmentAssignedId=${effectiveAssessmentId}`
      )

      if (!kvResp.ok) throw new Error(`Failed to fetch keys (${kvResp.status})`)

      const kvData = await kvResp.json()
      // //console.log('Object keys response:', kvData)

      // Check if we have valid rows in the response
      if (!kvData.data?.rows || !Array.isArray(kvData.data.rows) || kvData.data.rows.length === 0) {
        message.warning('No media files found for this assessment')
        return []
      }

      const rows = kvData.data.rows
      // //console.log('Object keys:', rows)

      // Match known patterns for audio and video files
      // Audio patterns: 'audio', 'Paragraph_reading', 'etc.
      // Video patterns: 'video', 'Video_respose', 'etc.
      const audioPatterns = ['audio', 'paragraph_reading', 'paragraph', 'reading']
      const videoPatterns = ['video', 'video_respose', 'webcam']

      // Find audio key by matching patterns (case insensitive)
      const audioKey = rows.find(r =>
        audioPatterns.some(pattern =>
          r.object_key.toLowerCase().includes(pattern.toLowerCase())
        )
      )?.object_key

      // //console.log('Audio key:', audioKey)

      // Find video key by matching patterns (case insensitive)
      const videoKey = rows.find(r =>
        videoPatterns.some(pattern =>
          r.object_key.toLowerCase().includes(pattern.toLowerCase())
        )
      )?.object_key

      // //console.log('Video key:', videoKey)

      // Build array of files to request URLs for
      const files = []
      if (audioKey) files.push({ filename: audioKey, type: 'audio' })
      if (videoKey) files.push({ filename: videoKey, type: 'video' })

      // If no files matched our patterns, provide helpful error message
      if (!files.length) {
        console.warn('Could not identify audio or video files from:', rows.map(r => r.object_key).join(', '))
        message.warning('Could not identify audio or video files from the assessment')
      }

      return files
    } catch (error) {
      console.error('Error fetching media keys:', error)
      message.error('Failed to fetch media keys')
      return []
    }
  }

  /**
   * Fetches signed URLs for media files from the API
   * @param {Array} files - Array of file objects with filename and type
   * @returns {Promise<Object>} Object containing audio and video URLs
   */
  const getMediaUrls = async (files) => {
    if (!files || !Array.isArray(files) || files.length === 0) {
      console.warn('No files provided to get URLs for')
      return { audio: null, video: null }
    }

    try {
      const urlResp = await fetch(`${process.env.STD_API_URL}students/assessments/getMediaUrls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files })
      })

      if (!urlResp.ok) {
        const errorText = await urlResp.text()
        throw new Error(`Failed to fetch media URLs: ${errorText}`)
      }

      const urlData = await urlResp.json()
      // //console.log('URL response:', urlData)

      // Validate response
      if (!urlData.data?.urls) {
        throw new Error('Invalid response format: missing data.urls')
      }

      const urls = urlData.data.urls

      // Extract the audio and video URLs using the file types
      const audioFile = files.find(f => f.type === 'audio')
      const videoFile = files.find(f => f.type === 'video')

      // The response from the API just has the filenames as keys without the audio_/video_ prefix
      const audioUrl = audioFile ? urls[audioFile.filename] || null : null
      const videoUrl = videoFile ? urls[videoFile.filename] || null : null

      // //console.log('Audio URL:', audioUrl)
      // //console.log('Video URL:', videoUrl)

      return { audio: audioUrl, video: videoUrl }
    } catch (error) {
      console.error('Error fetching media URLs:', error)
      message.error(`Failed to fetch media URLs: ${error.message}`)
      return { audio: null, video: null }
    }
  }

  const handleMediaView = async (mediaType) => {
    // Set modal visibility and selected media type
    setSelectedMediaType(mediaType)
    setAudioVideoVisible(true)

    if (!effectiveAssessmentId) {
      console.error('No assessment assigned ID found')
      message.error('No assessment assigned ID found')
      return
    }

    // If we already have the media files but not the URLs, fetch them
    if (mediaState.mediaFiles.length > 0 && (!mediaUrls.audio && !mediaUrls.video)) {
      try {
        await dispatch(fetchMediaUrls(mediaState.mediaFiles))
      } catch (error) {
        console.error('Error fetching media URLs:', error)
        message.error('Failed to fetch media URLs')
      }
    }

    // Verify that the requested media type is available
    if (mediaType === 'audio' && !audioAvailable) {
      message.warning('Audio recording is not available for this assessment')
    } else if (mediaType === 'video' && !videoAvailable) {
      message.warning('Video recording is not available for this assessment')
    }
  }

  // Add a handler to properly close the modal and reset state
  const handleAudioVideoModalClose = () => {
    setAudioVideoVisible(false)
    setSelectedMediaType(null)
    // Add a small delay to ensure the modal is fully closed before resetting
    setTimeout(() => {
      setSelectedMediaType(null)
    }, 100)
  }

  const formatDateTime = dateString => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  }

  const formatIfCalculated = (value, suffix = '') => {
    if (student && student.scoresCalculated === false) return '-'
    const num = Number(value)
    return Number.isFinite(num) ? `${num.toFixed(2)}${suffix}` : `0.00${suffix}`
  }

  // Safe toFixed helper to prevent "toFixed is not a function" errors
  const safeToFixed = (value, decimals = 2, suffix = '') => {
    const num = Number(value)
    return Number.isFinite(num) ? `${num.toFixed(decimals)}${suffix}` : '-'
  }

  // Handle selection change
  const handleDisplayTypeChange = value => {
    setDisplayType(value)
  }

  // Process IP addresses - show unique IPs only
  const processIpAddresses = (ipAddresses, snapshots) => {
    if (!ipAddresses || !ipAddresses.length) return []

    // Check if all IPs are the same
    const firstIp = ipAddresses[0]?.join(', ') || ''
    const allSame = ipAddresses.every(ip => ip.join(', ') === firstIp)

    if (allSame) {
      // If all IPs are the same, return just one entry
      return [
        {
          key: 0,
          ip: firstIp,
          timestamp: snapshots[0]?.capturedAt
            ? formatDateTime(snapshots[0].capturedAt)
            : 'N/A',
          occurredAt: 'All sessions',
          count: ipAddresses.length,
        },
      ]
    } else {
      // Otherwise, find unique IPs with their timestamps
      const uniqueIps = new Map()

      ipAddresses.forEach((ip, index) => {
        const ipString = ip.join(', ')
        if (!uniqueIps.has(ipString)) {
          uniqueIps.set(ipString, {
            key: index,
            ip: ipString,
            timestamp: snapshots[index]?.capturedAt
              ? formatDateTime(snapshots[index].capturedAt)
              : 'N/A',
            occurredAt: `Session ${index + 1}`,
            count: 1,
          })
        } else {
          const existing = uniqueIps.get(ipString)
          existing.count++
          existing.occurredAt = `Multiple sessions`
        }
      })

      return Array.from(uniqueIps.values())
    }
  }

  // Check if this is a behavior assessment by looking for behaviorProficiencies
  const isBehaviorAssessment =
    (student?.behaviorProficiencies &&
      Object.keys(student.behaviorProficiencies).length > 0) ||
    (student?.behaviorScores && Object.keys(student.behaviorScores).length > 0)

  // Check if this is an aptitude assessment by looking for aptitudeScores OR aptitude keys in sectionScores
  const isAptitudeAssessment = (() => {
    if (student?.aptitudeScores && typeof student.aptitudeScores === 'object') return true
    if (student?.sectionScores && typeof student.sectionScores === 'object') {
      const keys = Object.keys(student.sectionScores)
      const aptitudeKeys = ['critical', 'quantitative', 'logical']
      return keys.some(k => aptitudeKeys.includes(k.toLowerCase()))
    }
    return false
  })()
  const isRoleBasedAssessment = (student?.roleBasedScores && typeof student.roleBasedScores === 'object') ||
    (student?.assessmentType && (student.assessmentType.toLowerCase().includes('role_based') || student.assessmentType.toLowerCase().includes('rolebased')))
  const customScoresData = student?.customAssessmentScores || student?.customassessmentscores || student?.originalData?.customAssessmentScores || student?.originalData?.customassessmentscores
  const isCustomAssessment = (customScoresData && typeof customScoresData === 'object') ||
    (student?.assessmentType && student.assessmentType.toLowerCase().includes('custom'))


  const renderRoleBasedScoreCards = () => {
    // Support both roleBasedScores (direct) and sectionScores (from API)
    const roleBasedData = student?.roleBasedScores || student?.sectionScores
    if (!roleBasedData || typeof roleBasedData !== 'object') return null

    // Check if data has predefined keys (overallScore, mcqScore, etc.)
    const predefinedMapping = {
      overallScore: { title: 'Overall Score', subtitle: '(Weighted)' },
      mcqScore: { title: 'MCQ Score', subtitle: '(Multiple Choice)' },
      subjectiveScore: { title: 'Subjective Score', subtitle: '(Written Response)' },
      videoScore: { title: 'Video Score', subtitle: '(Video Response)' },
      codingScore: { title: 'Coding Score', subtitle: '(Programming)' }
    }

    const hasPredefinedKeys = Object.keys(predefinedMapping).some(k => roleBasedData.hasOwnProperty(k))

    let scoreEntries
    if (hasPredefinedKeys) {
      scoreEntries = Object.keys(predefinedMapping)
        .filter(key => roleBasedData.hasOwnProperty(key))
        .map(key => ({ key, title: predefinedMapping[key].title, subtitle: predefinedMapping[key].subtitle, value: roleBasedData[key] }))
    } else {
      // Dynamic section names from API sectionScores (e.g., "mcq question", "subjective question", "video response")
      const capitalize = (str) => str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      // Add overall score first
      const entries = [{ key: 'overall', title: 'Overall Score', subtitle: '(Average)', value: student?.totalScore }]
      Object.entries(roleBasedData).forEach(([key, value]) => {
        if (typeof value === 'number') {
          entries.push({ key, title: capitalize(key), subtitle: '', value })
        }
      })
      scoreEntries = entries.filter(e => e.value != null)
    }

    // For backward compat with old code path
    const availableScores = scoreEntries

    // Group scores into rows of 4
    const rows = chunkArray(scoreEntries, 4)

    return rows.map((rowEntries, rowIndex) => (
      <Row
        key={`rolebased-row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowEntries.map(entry => (
            <Col key={entry.key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={entry.value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{entry.title}</ScoreTitle>
                    {entry.subtitle && <SubText>{entry.subtitle}</SubText>}
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={entry.value}>
                      {safeToFixed(entry.value)}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        )}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {rowIndex === rows.length - 1 &&
          rowEntries.length < 4 &&
          Array(4 - rowEntries.length)
            .fill()
            .map((_, i) => (
              <Col key={`rolebased-empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))}
      </Row>
    ))
  }

  // Create custom assessment score cards
  const renderCustomAssessmentScoreCards = () => {
    const customData = customScoresData || student?.sectionScores
    if (!customData || typeof customData !== 'object') {
      // Fallback: show totalScore if available
      if (student?.totalScore != null) {
        return (
          <Row gutter={16} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={student.totalScore}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>Overall Score</ScoreTitle>
                    <SubText>(Total Percentage)</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={student.totalScore}>
                      {student.totalScore}%
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          </Row>
        )
      }
      return null
    }
    const sectionWiseStats = customData.sectionWiseStats || customData.sectionwisestats || {}

    // Prepare scores array - Overall percentage first, then sections
    const scores = []

    // Add overall percentage as the first card
    const overallPct = typeof customData.percentage === 'number' ? customData.percentage : (typeof student?.totalScore === 'number' ? student.totalScore : null)
    scores.push({
      key: 'overall',
      title: 'Overall Score',
      subtitle: '(Total Percentage)',
      value: overallPct != null ? overallPct : '-',
      displayValue: overallPct != null ? `${overallPct}%` : '-'
    })

    console.log("Scores", scores)

    // Add section-wise scores
    if (typeof sectionWiseStats === 'object' && Object.keys(sectionWiseStats).length > 0) {
      Object.keys(sectionWiseStats).forEach(sectionName => {
        const section = sectionWiseStats[sectionName]
        scores.push({
          key: section.section_id,
          title: section.section_name,
          subtitle: typeof section.total_attempted === 'number' ? `(${section.total_attempted}/${section.total_questions} Attempted)` : '-',
          value: typeof section.percentage === 'number' ? section.percentage : '-',
          displayValue: typeof section.percentage === 'number' ? `${section.percentage}%` : '-'
        })
      })
    }

    console.log("Scores", scores)
    // Group scores into rows of 4
    const rows = chunkArray(scores, 4)

    return rows.map((rowScores, rowIndex) => (
      <Row
        key={`custom-row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowScores.map(score => {
          return (
            <Col key={score.key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={score.value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{score.title}</ScoreTitle>
                    <SubText>{score.subtitle}</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={score.value}>
                      {score.displayValue}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        })}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {/* {rowIndex === rows.length - 1 &&
          rowScores.length < 4 &&
          Array(4 - rowScores.length)
            .fill()
            .map((_, i) => (
              <Col key={`custom-empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))} */}
      </Row>
    ))
  }
  // Function to chunk array into groups of n
  const chunkArray = (array, chunkSize) => {
    const result = []
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize))
    }
    return result
  }

  // Create dynamic behavior proficiency cards
  const renderBehaviorScoreCards = () => {
    if (!student?.behaviorProficiencies && !student?.behaviorScores) return null
    const useSource = student?.behaviorProficiencies
      ? 'behaviorProficiencies'
      : 'behaviorScores'
    const behaviorKeys = Object.keys(student[useSource]).sort()
    // Group keys into rows of 4
    const rows = chunkArray(behaviorKeys, 4)
    return rows.map((rowKeys, rowIndex) => (
      <Row
        key={`row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowKeys.map(key => {
          const value = student[useSource][key]
          return (
            <Col key={key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{key}</ScoreTitle>
                    <SubText>&nbsp;</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={value}>
                      {safeToFixed(value)}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        })}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {rowIndex === rows.length - 1 &&
          rowKeys.length < 4 &&
          Array(4 - rowKeys.length)
            .fill()
            .map((_, i) => (
              <Col key={`empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))}
      </Row>
    ))
  }

  // Create aptitude score cards
  const renderAptitudeScoreCards = () => {
    let aptitudeData = null

    // Priority 1: Direct aptitudeScores object
    if (student?.aptitudeScores && typeof student.aptitudeScores === 'object') {
      aptitudeData = { ...student.aptitudeScores }
    }
    // Priority 2: Map from sectionScores if available
    else if (student?.sectionScores && typeof student.sectionScores === 'object') {
      const s = student.sectionScores
      aptitudeData = {}

      const getVal = (k) => {
        const v = s[k]
        if (typeof v === 'object' && v?.average !== undefined) return v.average
        if (typeof v === 'number') return v
        return undefined
      }

      const critical = getVal('critical')
      const quantitative = getVal('quantitative')
      const logical = getVal('logical')

      if (critical !== undefined) aptitudeData.criticalReasoningPercentage = critical
      if (quantitative !== undefined) aptitudeData.quantitativePercentage = quantitative
      if (logical !== undefined) aptitudeData.logicalReasoningPercentage = logical

      if (student.totalAvgScore !== undefined) aptitudeData.overallPercentage = student.totalAvgScore
      else if (student.totalScore !== undefined) aptitudeData.overallPercentage = student.totalScore
    }

    if (!aptitudeData || Object.keys(aptitudeData).length === 0) return null

    // Define the order and display names for aptitude scores
    const scoreMapping = {
      overallPercentage: { title: 'Overall Score', subtitle: '(Total Percentage)' },
      criticalReasoningPercentage: { title: 'Critical Reasoning', subtitle: '(Percentage)' },
      quantitativePercentage: { title: 'Quantitative Ability', subtitle: '(Percentage)' },
      logicalReasoningPercentage: { title: 'Logical Reasoning', subtitle: '(Percentage)' }
    }

    // Get available scores in the defined order
    const availableScores = Object.keys(scoreMapping).filter(key =>
      aptitudeData.hasOwnProperty(key) && aptitudeData[key] !== undefined && aptitudeData[key] !== null
    )

    // Group scores into rows of 4
    const rows = chunkArray(availableScores, 4)

    return rows.map((rowKeys, rowIndex) => (
      <Row
        key={`aptitude-row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowKeys.map(key => {
          const value = aptitudeData[key]
          const { title, subtitle } = scoreMapping[key]
          return (
            <Col key={key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{title}</ScoreTitle>
                    <SubText>{subtitle}</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={value}>
                      {safeToFixed(value, 2, '%')}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        })}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {rowIndex === rows.length - 1 &&
          rowKeys.length < 4 &&
          Array(4 - rowKeys.length)
            .fill()
            .map((_, i) => (
              <Col key={`aptitude-empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))}
      </Row>
    ))
  }

  const handleDownloadReport = async (assignedId = null) => {
    if (hasSingleDiagnosisAttempt) {
      message.warning(`${SINGLE_DIAGNOSIS_ATTEMPT_MESSAGE} - the report needs both diagnosis papers`)
      return
    }
    // For non-diagnosis downloads, check scores
    if (!assignedId) {
      if (student.scoresCalculated === false) {
        message.warning('Report is not available - assessment scores are currently being processed')
        return
      }
      if (!reportAvailable) {
        message.warning('Report is not available for this assessment')
        return
      }
    }

    try {
      setDownloadingReport(true)
      const API_URL = process.env.STD_API_URL
      const token = localStorage.getItem('token')

      // Add a trailing slash if needed
      const baseUrl = API_URL.endsWith('/') ? API_URL : `${API_URL}/`

      // Use the passed assignedId (for diagnosis dropdown) or fall back to effectiveAssessmentId
      const assessmentAssignedId = assignedId || effectiveAssessmentId

      // Handle different student ID property names
      const rawStudentNumber = student?.studentNumber ?? student?.studentId ?? student?.id
      const studentNumber = (rawStudentNumber !== null && rawStudentNumber !== undefined && rawStudentNumber !== '-') ? rawStudentNumber : null

      if (!assessmentAssignedId || studentNumber === null) {
        message.error('Assessment ID or Student ID is missing')
        setDownloadingReport(false)
        return
      }

      // Build request payload
      const payload = {
        assessment_assigned_id: assessmentAssignedId,
        student_id: studentNumber,
      }

      // Log the request payload for debugging
      //console.log('Sending PDF request with payload:', payload)

      const response = await axios.post(
        `${baseUrl}students/assessments/generatePDFReport`,
        payload,
        {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      )

      // Error handling for missing or bad response
      if (!response) {
        message.error('No response from server')
        setDownloadingReport(false)
        return
      }
      if (response.status !== 200) {
        message.error(`Failed to download report`)
        setDownloadingReport(false)
        return
      }
      if (!response.data) {
        message.error('No file data received')
        setDownloadingReport(false)
        return
      }

      let fileName = `${student.id}_report.pdf`
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/)
        if (filenameMatch && filenameMatch[1]) {
          fileName = filenameMatch[1].trim()
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download PDF error:', error)

      // More detailed error reporting
      if (error.response) {
        console.error('Error response:', error.response.data)
        console.error('Status:', error.response.status)
        message.error(`Failed to download report: ${error.response.status} ${error.response.statusText}`)
      } else {
        message.error('Failed to download report')
      }
    } finally {
      setDownloadingReport(false)
    }
  }

  if (!student) return null

  // Read proctoring across ALL sessions of the attempt, not just sessions[0]
  // (see proctoringView.js).
  const proctoringSessions = proctoringData?.sessions || []
  const snapshots = collectSnapshots(proctoringSessions)
  // Backend already aggregates face detection across every session.
  const faceDetectionSummary = proctoringData?.overallSummary || {}
  // const phoneDetectionSummary =  // Phone detection removed
  //   proctoringData?.sessions?.[0]?.phoneDetectionSummary || {}
  const sessionInfo = primarySession(proctoringSessions)
  const ipAddresses = collectIpAddresses(proctoringSessions)

  // Add debug logs RIGHT BEFORE the return statement
  console.log('Proctoring Data:', proctoringData)
  console.log('Snapshots:', snapshots)
  console.log('Face Detection Summary:', faceDetectionSummary)
  console.log('allowProctoring:', allowProctoring)
  console.log('proctoringLoading:', proctoringLoading)
  console.log('Should show proctoring section:', proctoringData && allowProctoring)
  console.log('Display type:', displayType)

  // Get unique IPs
  const uniqueIpData = processIpAddresses(ipAddresses, snapshots)

  return (
    <>
      <Drawer
        visible={visible}
        onClose={onClose}
        placement="right"
        width={720}
        title={null}
        headerStyle={{ display: 'none' }}
        bodyStyle={{ padding: 0, overflowY: 'auto' }}
      >
        {/* Custom header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: '#fff',
          zIndex: 10,
        }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
            Assessment Report
          </span>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: 20, lineHeight: 1 }}>×</span>
        </div>
        <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 16px',
            background: '#f8f9fc',
            borderRadius: 10,
            border: '1px solid #eef0f6',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, color: '#3949ab',
              flexShrink: 0,
              overflow: 'hidden',
            }}>
              {(student.student_img_url || student.profileUrl) ? (
                <img src={student.student_img_url || student.profileUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (student?.name || student?.studentName || 'S').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', lineHeight: '20px' }}>
                {student?.name || student?.studentName || '-'}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                {[student.email, student.phone].filter(Boolean).join(' · ') || '-'}
              </div>
            </div>
            {/* Disabled buttons swallow mouse events, so the tooltip hangs off a wrapper span */}
            <Tooltip title={downloadReportTitle}>
              <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                    height: 36,
                    padding: '0 14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: isReportDownloadable ? '#3249D7' : '#9CA3AF',
                    borderColor: isReportDownloadable ? '#3249D7' : '#9CA3AF',
                    borderRadius: 8,
                    boxShadow: isReportDownloadable ? '0 1px 2px rgba(50, 73, 215, 0.2)' : 'none',
                  }}
                  onClick={() => handleDownloadReport()}
                  loading={downloadingReport}
                  disabled={!isReportDownloadable}
                >
                  Download Report
                </Button>
              </span>
            </Tooltip>
          </div>
          {isDiagnosis && (
            <div style={{ marginTop: '16px', marginBottom: '8px' }}>
              <Text strong style={{ marginRight: 8 }}>Select Assessment:</Text>
              <Select
                value={selectedAssessmentId}
                onChange={(value) => setSelectedAssessmentId(value)}
                style={{ width: 280 }}
                placeholder="Select an assessment"
              >
                {student?.assessmentAssignedId1 && (
                  <Option value={student.assessmentAssignedId1}>
                    Assessment #1 {student?.isScoreCalculated1 === false ? '(Calculating...)' : '(Completed)'}
                  </Option>
                )}
                {student?.assessmentAssignedId2 && (
                  <Option value={student.assessmentAssignedId2}>
                    Assessment #2 {student?.isScoreCalculated2 === false ? '(Calculating...)' : '(Completed)'}
                  </Option>
                )}
              </Select>
            </div>
          )}
        </div>        <Divider />

        {/* Latest Score section - Remove the separate View Proctoring button */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Latest Score</div>
            {/* Render the media control buttons with Redux-based state */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                // disabled={!allowProctoring || isBehaviorAssessment || isAptitudeAssessment}
                onClick={handleViewProctoring}
                loading={proctoringLoading}
              >
                {proctoringData ? 'Refresh Proctoring' : 'View Proctoring'}
              </Button>
              <Button
                type="primary"
                icon={<AudioFilled />}
                onClick={() => handleMediaView('audio')}
                disabled={mediaLoading || !audioAvailable || isAptitudeAssessment}
                loading={mediaLoading && selectedMediaType === 'audio'}
                title={!audioAvailable || isAptitudeAssessment ? "Audio not available for this assessment" : "View audio recording"}
              />
              <Button
                type="primary"
                icon={<VideoCameraFilled />}
                onClick={() => handleMediaView('video')}
                disabled={mediaLoading || !videoAvailable || isAptitudeAssessment}
                loading={mediaLoading && selectedMediaType === 'video'}
                title={!videoAvailable || isAptitudeAssessment ? "Video not available for this assessment" : "View video recording"}
              />
            </div>
          </div>
          <Text type="secondary">
            Timestamp:{' '}
            {new Date(student.lastAttemptDate || Date.now()).toLocaleString()}
          </Text>
        </div>

        <Divider />

        {/* Proctoring Status section */}
        {/* Only show Proctoring Status for non-behavior and non-aptitude assessments */}
        {allowProctoring && !isBehaviorAssessment && !isAptitudeAssessment && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Proctoring Status</div>
            {/* Check if proctoring has not been calculated yet */}
            {(student.proctoringCalculated === false || student.flagged === null ||
              student.proctoringStatus === '-' || student.proctoring === '-' || student.proctoringStatus === 'Pending') ? (
              <Badge
                status="processing"
                text="Evaluating..."
              />
            ) : (
              <>
                <Badge
                  status={student.flagged === true ? 'error' : 'success'}
                  text={student.flagged === true ? 'Poor' : 'Good'}
                />
                {student.flagged === true && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="danger">
                      This assessment was flagged for suspicious activity.
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <Divider />

        {/* Conditional rendering of assessment scores */}
        {isBehaviorAssessment ? (
          // Dynamic behavior score cards
          renderBehaviorScoreCards()
        ) : isAptitudeAssessment ? (
          // Dynamic aptitude score cards
          renderAptitudeScoreCards()
        ) : isRoleBasedAssessment ? (
          // Dynamic role-based score cards
          renderRoleBasedScoreCards()
        ) : isCustomAssessment ? (
          // Dynamic custom assessment score cards
          renderCustomAssessmentScoreCards()
        ) : (
          // Language Assessment Cards - supports both sectionScores (new) and flat fields (old)
          <>
            {(() => {
              const sectionScores = student.sectionScores || {}
              const hasNewFormat = Object.keys(sectionScores).length > 0 || student.totalScore !== undefined

              const getScore = (newKey, oldKey) => {
                // Try new format first (sectionScores object)
                if (Object.keys(sectionScores).length > 0) {
                  const val = sectionScores[newKey]
                  if (typeof val === 'object' && val?.average !== undefined) return val.average
                  if (typeof val === 'number') return val
                }
                // Always fall back to flat fields if sectionScores key not found
                return typeof student[oldKey] === 'number' ? student[oldKey] : null
              }

              const totalScore = student.totalScore ?? student.totalAvgScore
              const reading = getScore('reading', 'readingComprehensionScore') ?? getScore('reading', 'readingAbilityScore')
              const listening = getScore('listening', 'listeningAbilityScore')
              const speaking = getScore('speaking', 'englishVerbalScore')
              const writing = getScore('writing', 'writingScore')

              // Normalize enabledSections to group-level
              const SUBSECTION_TO_GROUP = {
                'Paragraph Reading': 'Reading', 'Audio Question': 'Listening',
                'Video Response': 'Speaking', 'Question Based Response': 'Writing',
                'Email Writing': 'Writing', 'Dictation': 'Writing',
                'Sentence Completion': 'Writing', 'Sentence Build': 'Writing',
              };
              const normalizedEnabled = enabledSectionsProp && enabledSectionsProp.length > 0
                ? [...new Set(enabledSectionsProp.map(s => SUBSECTION_TO_GROUP[s] || s))]
                : ['Reading', 'Listening', 'Speaking', 'Writing'];
              const isEnabled = (section) => normalizedEnabled.includes(section);

              return (
                <Row gutter={16} style={{ marginBottom: 32 }}>
                  <Col span={6}>
                    <ScoreCard score={totalScore ?? 0}>
                      <CardContent>
                        <TitleSection>
                          <ScoreTitle level={5}>Total Score</ScoreTitle>
                          <SubText>(Average of all sections)</SubText>
                        </TitleSection>
                        <ValueSection>
                          <ScoreValue score={totalScore ?? 0}>
                            {typeof totalScore === 'number' ? totalScore.toFixed(2) : '-'}
                          </ScoreValue>
                        </ValueSection>
                      </CardContent>
                    </ScoreCard>
                  </Col>
                  {isEnabled('Reading') && (
                    <Col span={6}>
                      <ScoreCard score={reading ?? 0}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Reading</ScoreTitle>
                            <SubText>(Comprehension)</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={reading ?? 0}>
                              {typeof reading === 'number' ? reading.toFixed(2) : '-'}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                  {isEnabled('Listening') && (
                    <Col span={6}>
                      <ScoreCard score={listening ?? 0}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Listening</ScoreTitle>
                            <SubText>&nbsp;</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={listening ?? 0}>
                              {typeof listening === 'number' ? listening.toFixed(2) : '-'}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                  {isEnabled('Speaking') && (
                    <Col span={6}>
                      <ScoreCard score={speaking ?? 0}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Speaking</ScoreTitle>
                            <SubText>&nbsp;</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={speaking ?? 0}>
                              {typeof speaking === 'number' ? speaking.toFixed(2) : '-'}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                  {isEnabled('Writing') && writing !== null && typeof writing === 'number' && (
                    <Col span={6} style={{ marginTop: 16 }}>
                      <ScoreCard score={writing}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Writing</ScoreTitle>
                            <SubText>&nbsp;</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={writing}>
                              {writing.toFixed(2)}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                </Row>
              )
            })()}
          </>
        )}

        {/* Progression Graphs - show only relevant type */}
        {(commProgression.length > 0 || aptProgression.length > 0) && (
          <>
            <Divider />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginBottom: 16 }}>
              {isCommunicationType ? 'Communication Improvement' : isAptitudeType ? 'Aptitude Improvement' : 'Improvement'}
            </div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {commProgression.length > 0 && !isAptitudeType && (
                <Col span={aptProgression.length > 0 && !isCommunicationType ? 12 : 24}>
                  <ProgressionGraph
                    data={commProgression}
                    title="Communication Improvement"
                    currentAssessmentId={effectiveAssessmentId}
                    delta={commDelta?.value}
                    height={250}
                    showPractice={false}
                  />
                </Col>
              )}
              {aptProgression.length > 0 && !isCommunicationType && (
                <Col span={commProgression.length > 0 && !isAptitudeType ? 12 : 24}>
                  <ProgressionGraph
                    data={aptProgression}
                    title="Aptitude Improvement"
                    currentAssessmentId={effectiveAssessmentId}
                    delta={aptDelta?.value}
                    height={250}
                    showPractice={false}
                  />
                </Col>
              )}
            </Row>
          </>
        )}

        {/* Proctoring Snapshots Section - Only show for Communication assessments */}
        {proctoringLoading ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin size="large" tip="Loading proctoring data..." />
          </div>
        ) : proctoringData && proctoringData.proctoringPending ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Text strong>{proctoringData.message}</Text>
                  <Badge status="processing" text="Calculation in progress" />
                </div>
              }
            />
          </div>
        ) : proctoringData ? (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginRight: 12 }}>
                  Proctoring Data
                </div>
                <Select
                  value={displayType}
                  onChange={handleDisplayTypeChange}
                  style={{ width: 180 }}
                >
                  <Option value="snapshots">Snapshots</Option>
                  <Option value="ipAddresses">IP Addresses</Option>
                </Select>
              </div>
            </div>

            {/* Conditional rendering based on selected display type */}
            {displayType === 'snapshots' ? (
              <>
                {/* Face Detection Statistics */}
                {faceDetectionSummary && Object.keys(faceDetectionSummary).length > 0 && (
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    {/* Face Detection Statistics */}
                    <Col span={24}>
                      <Title level={5}>Face Detection Summary</Title>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Face Detection Rate"
                          value={faceDetectionSummary.faceDetectionRate || 'N/A'}
                          valueStyle={{
                            color:
                              faceDetectionSummary.faceDetectionRate &&
                                parseInt(faceDetectionSummary.faceDetectionRate) < 80
                                ? '#E35E50'
                                : '#0FB680',
                            fontWeight: 'bold',
                          }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Single Face (Valid)"
                          value={faceDetectionSummary.singleFace || 0}
                          valueStyle={{ color: '#0FB680' }}
                          suffix={`/ ${faceDetectionSummary.totalSnapshots || 0}`}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Multiple/No Faces"
                          value={
                            (faceDetectionSummary.multipleFaces || 0) +
                            (faceDetectionSummary.noFaceDetected || 0)
                          }
                          valueStyle={{ color: '#E35E50' }}
                          suffix={`/ ${faceDetectionSummary.totalSnapshots || 0}`}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Phone Detection Summary — removed */}

                {/* Snapshots Grid */}
                {snapshots && snapshots.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {snapshots.map((snapshot, index) => (
                      <Col span={8} key={snapshot.id}>
                        <SnapshotCard>
                          {snapshot.imageUrl?.data?.parUrl ? (
                            <Image
                              className="snapshot-image"
                              src={snapshot.imageUrl.data.parUrl}
                              alt={`Snapshot ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '150px',
                                objectFit: 'cover',
                              }}
                              preview={{
                                mask: 'View Image'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                height: 150,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px'
                              }}
                            >
                              <Empty
                                description="No image"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            </div>
                          )}
                          <div className="snapshot-info">
                            <div>
                              <Text strong>Time: </Text>
                              <Text>{formatDateTime(snapshot.capturedAt)}</Text>
                            </div>
                            <div>
                              <Text strong>Face Count: </Text>
                              {snapshot.faceCount === 1 ? (
                                <Text type="success">
                                  <CheckCircleFilled /> {snapshot.faceCount} (Valid)
                                </Text>
                              ) : snapshot.faceCount === 0 ? (
                                <Text type="danger">
                                  <CloseCircleFilled /> None
                                </Text>
                              ) : (
                                <Text type="warning">
                                  <CloseCircleFilled /> {snapshot.faceCount} (Multiple)
                                </Text>
                              )}
                            </div>
                            {/* Phone detection removed */}
                          </div>
                        </SnapshotCard>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty description="No snapshots available" />
                )}
              </>
            ) : (
              // Display IP information
              <div>
                {sessionInfo && Object.keys(sessionInfo).length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Assessment Status:
                      </Text>
                      <Tag
                        color={
                          proctoringData.assessmentStatus === 'Completed'
                            ? 'success'
                            : 'processing'
                        }
                      >
                        {proctoringData.assessmentStatus || 'N/A'}
                      </Tag>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Browser:
                      </Text>
                      <Text>{sessionInfo.browserInfo || 'N/A'}</Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Session Duration:
                      </Text>
                      <Text>{sessionInfo.sessionDuration || 'N/A'}</Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Session Start:
                      </Text>
                      <Text>
                        {sessionInfo.sessionStart
                          ? formatDateTime(sessionInfo.sessionStart)
                          : 'N/A'}
                      </Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Session End:
                      </Text>
                      <Text>
                        {sessionInfo.sessionEnd
                          ? formatDateTime(sessionInfo.sessionEnd)
                          : 'N/A'}
                      </Text>
                    </div>
                  </Card>
                )}

                <Card title="IP Addresses" extra={<GlobalOutlined />}>
                  {uniqueIpData && uniqueIpData.length > 0 ? (
                    <Table
                      dataSource={uniqueIpData}
                      columns={[
                        {
                          title: 'IP Address',
                          dataIndex: 'ip',
                          key: 'ip',
                          render: text => <Text copyable>{text}</Text>,
                        },
                        {
                          title: 'First Detected',
                          dataIndex: 'timestamp',
                          key: 'timestamp',
                        },
                        {
                          title: 'Occurrence',
                          dataIndex: 'count',
                          key: 'count',
                          render: text => (text > 1 ? `${text} times` : '1 time'),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty description="No IP address data available" />
                  )}
                </Card>
              </div>
            )}
          </>
        ) : null}


        </div>
      </Drawer>

      <AudioVideoModal
        visible={audioVideoVisible}
        onClose={handleAudioVideoModalClose}
        mediaType={selectedMediaType}
        audioUrl={mediaUrls?.audio}
        videoUrl={mediaUrls?.video}
        loading={mediaLoading}
      />
    </>
  )
}

export default StudentReportModal
